/**
 * Mid-turn progress of a streamed chat turn, for the chat Status panel (`lib/chat-status.ts`).
 *
 * The stream carries no tool events, but three of its events follow a finished tool step: `cards` and
 * `cardRow` are sent once a step's tools have run, and a new `messageStart` opens the model call that reads
 * their results, so the step's TOOL_USE rows are persisted by then. Re-reading what the conversation created
 * on these is what lets a band's space, team and routines appear one step at a time instead of all at once
 * when the turn ends.
 *
 * Import-free on purpose: `use-chat` reports here, and its tests must not pull the status panel's graph.
 */
const TOOL_STEP_EVENTS = new Set(['cards', 'cardRow', 'messageStart']);

export const isToolStepEvent = (type: string | undefined): boolean => type !== undefined && TOOL_STEP_EVENTS.has(type);

type Listener = (conversationId: string) => void;
const listeners = new Set<Listener>();

export function reportTurnEvent(conversationId: string | null | undefined, type: string | undefined): void {
  if (!conversationId || !isToolStepEvent(type)) return;
  listeners.forEach((listener) => listener(conversationId));
}

export function onTurnProgress(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export interface Timers {
  now: () => number;
  setTimeout: (fn: () => void, ms: number) => unknown;
}

/**
 * Runs `fn(key)` at once, then at most once per `windowMs` for that key, with one trailing run when calls came
 * in during the window. A turn sends these events in bursts, and the trailing run is what reads the last step.
 */
export function keyedThrottle(fn: (key: string) => void, windowMs: number, timers: Timers): (key: string) => void {
  const last = new Map<string, number>();
  const pending = new Set<string>();
  return (key) => {
    if (pending.has(key)) return;
    const elapsed = timers.now() - (last.get(key) ?? -Infinity);
    if (elapsed >= windowMs) {
      last.set(key, timers.now());
      fn(key);
      return;
    }
    pending.add(key);
    timers.setTimeout(() => {
      pending.delete(key);
      last.set(key, timers.now());
      fn(key);
    }, windowMs - elapsed);
  };
}
